import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    makeStyles,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { Block, Heading } from '@klp/design';

import { Sak, SakStatus } from '../classes';
import type { ISakStatus } from '../classes/SakStatus';
import { useUpdateSakStatusMutation } from '../rtkApi';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        backgroundColor: theme.palette.primary.veryLight,
        borderBottom: '1px solid transparent',
        borderBottomColor: theme.palette.primary.medium,
        borderTop: '1px solid transparent',
        borderTopColor: theme.palette.primary.medium,
    },
    buttonGroup: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignContent: 'center',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap' as const,
        flexDirection: 'row' as const,
    },
    button: {
        margin: theme.spacing(1),
    },
    iconLeft: {
        marginRight: theme.spacing(1),
    },
    iconRight: {
        marginLeft: theme.spacing(1),
    },
}));

interface IProps {
    sak?: Sak;
}

export const SakSingleActions: React.FC<IProps> = ({ sak }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState<keyof ISakStatus>(
        SakStatus.Active.key,
    );

    const [updateStatus, { isError, isLoading: isUpdating }] =
        useUpdateSakStatusMutation();

    const handleClose = () => {
        setDialogOpen(false);
    };

    const showDialog = (type: keyof ISakStatus) => () => {
        setDialogOpen(true);
        setDialogType(type);
    };

    const dispatchAction = (status: keyof ISakStatus) => async () => {
        if (sak) {
            try {
                await updateStatus({ sakId: sak.id, status }).unwrap();

                handleClose();
            } catch (error) {
                // Error handled by mutation
            }
        }
        return status;
    };

    useEffect(() => {
        if (isError) {
            showToast({
                body: t('general.errorMessage'),
                type: 'error',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError]);

    if (!sak) {
        return null;
    }

    const { status } = sak;
    const isReadonlySakstatus = status === SakStatus.Merged.mapped;

    return (
        <>
            <Dialog
                fullScreen={fullScreen}
                fullWidth={true}
                maxWidth="xs"
                open={dialogOpen}
            >
                <DialogTitle
                    id="responsive-dialog-title"
                    disableTypography={true}
                >
                    <Heading translation={`sak.dialog.${dialogType}.title`} />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(`sak.dialog.${dialogType}.description`)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isUpdating}
                    >
                        {t('general.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={dispatchAction(dialogType)}
                        disabled={isUpdating}
                        startIcon={
                            isUpdating ? (
                                <CircularProgress size={20} />
                            ) : undefined
                        }
                    >
                        {t(`sak.dialog.${dialogType}.action`)}
                    </Button>
                </DialogActions>
            </Dialog>

            <Block
                className={classes.container}
                marginVertical="compact"
                paddingVertical="tiny"
            >
                <Grid container={true}>
                    <Grid item={true} xs={true} className={classes.buttonGroup}>
                        {[SakStatus.Active, SakStatus.Closed].map(
                            (sakStatus) => (
                                <Button
                                    key={sakStatus.key}
                                    className={classes.button}
                                    variant="outlined"
                                    onClick={showDialog(sakStatus.key)}
                                    disabled={
                                        isReadonlySakstatus ||
                                        sakStatus.mapped === status
                                    }
                                    startIcon={Sak.icon(sakStatus.key)}
                                >
                                    {t(`sak.actions.${sakStatus.key}`)}
                                </Button>
                            ),
                        )}
                    </Grid>
                </Grid>
            </Block>
        </>
    );
};
